import React, { createContext, useState } from 'react';
import { startCase } from 'lodash';
import { Modal } from 'components/Modal';
import { Text } from 'components/Typography';

import { AnsweringSubscriptionTag } from '@bb/domain/dist/types';
import type { EnhancedAnsweringServiceType } from '@/app-package';

type preBookingInformationType = string;
export type CrossOrgAnsweringContextType = {
  answeringConfirming: boolean;
  answeringConfirm: (target: targetType, cb: () => void) => () => void;
};
export type targetType = {
  type: 'agent' | 'listing' | 'brokerage';
  subscriptionTag?: AnsweringSubscriptionTag | EnhancedAnsweringServiceType;
  listingOrgHasPrebooking?: boolean;
  preBookingInformation?: preBookingInformationType;
};

const CrossOrgAnsweringContext =
  createContext<CrossOrgAnsweringContextType>(null);

export default CrossOrgAnsweringContext;

export const CrossOrgAnsweringContextProvider: React.FC = ({ children }) => {
  const [answeringConfirming, setAnsweringConfirming] = useState(false);

  const getModalDetails = (target: targetType) => {
    const { subscriptionTag, type } = target;

    if (subscriptionTag === AnsweringSubscriptionTag.OFF) {
      return {
        title: 'Appointment desk turned off',
        content: `This ${type} has the appointment desk turned off, are you sure you want to take an action on it?`,
      };
    }

    let content;
    switch (type) {
      case 'listing':
        content = `This ${type} might be represented by another showing service, please ask agent to book via MLS or get in touch with the listing agent.`;
        break;
      default:
        content = `This ${type} might be represented by another showing service, please get in touch with the ${type}.`;
    }

    return {
      title: `${startCase(type)} not represented by BrokerBay`,
      content,
    };
  };

  const renderPreBookingInfoModal = (
    preBookingInformation: preBookingInformationType,
    cb: () => void,
  ) => {
    Modal.confirm({
      icon: null,
      zIndex: 4000,
      title: 'Important Information',
      content: `${preBookingInformation}`,
      okText: 'Continue to booking',
      cancelText: 'Cancel',
      closable: true,
      onOk: () => {
        setAnsweringConfirming(false);
        cb();
      },
      okButtonProps: {
        // @ts-ignore
        'data-cy': 'answering-prebooking-confirm',
      },
      onCancel: () => setAnsweringConfirming(false),
      cancelButtonProps: {
        // @ts-ignore
        'data-cy': 'answering-prebooking-cancel',
      },
    });
  };
  // to decide if it should show next prebooking modal and execute the callback or should execute callback instantly
  const decideNextActions = (
    shouldShowPreBookingModal: boolean,
    preBookingInformation: preBookingInformationType | undefined,
    cb: () => void,
  ) => {
    if (shouldShowPreBookingModal) {
      renderPreBookingInfoModal(preBookingInformation!, cb);
    } else {
      cb();
    }
  };

  const answeringConfirm = (target: targetType, cb: () => void) => () => {
    let shouldShowPreBookingModal = false;
    let preBookingInformation: string | undefined;
    if (target.type === 'listing') {
      ({ preBookingInformation } = target);
      shouldShowPreBookingModal =
        Boolean(target.listingOrgHasPrebooking) &&
        Boolean(preBookingInformation);
    }

    if (
      target.subscriptionTag &&
      (target.subscriptionTag === AnsweringSubscriptionTag.UNC ||
        target.subscriptionTag === AnsweringSubscriptionTag.OFF)
    ) {
      setAnsweringConfirming(true);
      const { title, content } = getModalDetails(target);
      Modal.confirm({
        title,
        content: <Text dataCy="answering-confirmation-content">{content}</Text>,
        zIndex: 4000,
        okText:
          target.subscriptionTag === AnsweringSubscriptionTag.UNC
            ? 'Proceed anyway'
            : 'Continue',
        onOk: () => {
          setAnsweringConfirming(false);
          decideNextActions(
            shouldShowPreBookingModal,
            preBookingInformation,
            cb,
          );
        },
        // @ts-ignore
        okButtonProps: { 'data-cy': 'answering-confirmation-confirm' },
        onCancel: () => setAnsweringConfirming(false),
        cancelButtonProps: {
          // @ts-ignore
          'data-cy': 'answering-confirmation-cancel',
        },
        maskClosable: true,
      });
    } else {
      decideNextActions(shouldShowPreBookingModal, preBookingInformation!, cb);
    }
  };

  return (
    <CrossOrgAnsweringContext.Provider
      value={{ answeringConfirming, answeringConfirm }}
    >
      {children}
    </CrossOrgAnsweringContext.Provider>
  );
};
